import { IScheduleResponse, IPeriodResponse, EWeekDay } from 'src/apis/academics/academicsSchedules.interfaces';
import { IClassResponse, ICourseResponse } from 'src/apis/academics/academicsClassesAndCourses.interfaces';
import { ILessonPlanApiResponse, ELessonPlanStatus } from 'src/apis/academics/academicsLessonPlans.interfaces';
import {
  GuardianAssessment,
  GuardianHomework,
  GuardianAttendance,
  GuardianLibraryBook,
} from 'src/apis/guardian/sub-apis/useGuardianAcademicService';

export const mockGuardianAssessments: GuardianAssessment[] = [
  {
    id: 'assess-1',
    subject: 'Mathematics',
    title: 'Unit Test 1',
    score: 85,
    maxScore: 100,
    date: '2024-01-15',
    grade: 'A',
  },
  {
    id: 'assess-2',
    subject: 'Science',
    title: 'Quarterly Exam',
    score: 92,
    maxScore: 100,
    date: '2024-01-10',
    grade: 'A+',
  },
  {
    id: 'assess-3',
    subject: 'English',
    title: 'Unit Test 2',
    score: 78,
    maxScore: 100,
    date: '2024-01-20',
    grade: 'B+',
  },
];

export const mockGuardianHomework: GuardianHomework[] = [
  {
    id: 'hw-1',
    subject: 'Science',
    title: 'Chapter 5 Exercises',
    dueDate: '2024-02-01',
    status: 'pending',
    description: 'Complete exercises 1-10 from Chapter 5',
  },
  {
    id: 'hw-2',
    subject: 'Mathematics',
    title: 'Algebra Problems',
    dueDate: '2024-01-25',
    status: 'completed',
    description: 'Solve problems 1-20',
  },
  {
    id: 'hw-3',
    subject: 'English',
    title: 'Essay Writing',
    dueDate: '2024-01-22',
    status: 'overdue',
    description: 'Write an essay on "My Favorite Season"',
  },
];

export const mockGuardianAttendance: GuardianAttendance[] = [
  {
    date: '2024-01-20',
    status: 'present',
    period: 'Full Day',
  },
  {
    date: '2024-01-19',
    status: 'present',
    period: 'Full Day',
  },
  {
    date: '2024-01-18',
    status: 'late',
    period: 'Full Day',
  },
  {
    date: '2024-01-17',
    status: 'absent',
    period: 'Full Day',
  },
  {
    date: '2024-01-16',
    status: 'present',
    period: 'Full Day',
  },
];

export const mockGuardianLibraryBooks: GuardianLibraryBook[] = [
  {
    id: 'book-1',
    title: 'Introduction to Science',
    author: 'Rajesh Kumar',
    isbn: '978-1234567890',
    available: true,
  },
  {
    id: 'book-2',
    title: 'Mathematics Made Easy',
    author: 'Ananya Patel',
    isbn: '978-0987654321',
    available: false,
    dueDate: '2024-02-15',
  },
  {
    id: 'book-3',
    title: 'History of India',
    author: 'Vikram Singh',
    isbn: '978-1122334455',
    available: true,
  },
];


// Generate periods for a class schedule with multiple days
const generateGuardianSchedulePeriods = (
  classType: '7th' | '8th' | '9th' = '7th',
  section: 'A' | 'B' = 'A'
): IPeriodResponse[] => {
  const periods: IPeriodResponse[] = [];

  // Different subjects for different classes
  const subjects7th = ['English', 'Tamil', 'Maths', 'Physics', 'Biology', 'P.E.T', 'French', 'Hindi', 'History', 'Geography'];
  const subjects8th = ['English', 'Tamil', 'Maths', 'Physics', 'Chemistry', 'Biology', 'P.E.T', 'French', 'History', 'Geography', 'Civics'];
  const subjects9th = ['English', 'Tamil', 'Maths', 'Physics', 'Chemistry', 'Biology', 'P.E.T', 'French', 'History', 'Geography', 'Economics'];

  const subjects = classType === '7th' ? subjects7th : classType === '8th' ? subjects8th : subjects9th;

  const teachers = [
    'Mrs. Smith', 'Mr. Kumar', 'Ms. Patel', 'Dr. Rao', 'Mrs. Sharma',
    'Mr. Singh', 'Ms. Desai', 'Mrs. Reddy', 'Mr. Iyer', 'Mrs. Nair', 'Dr. Menon'
  ];
  const rooms = ['Room 101', 'Room 102', 'Room 103', 'Lab 1', 'Lab 2', 'Ground', 'Room 201', 'Room 202', 'Room 301', 'Room 302'];

  // Time slots: 8:30 AM to 4:30 PM
  const timeSlots = [
    { start: '08:30', end: '09:20', period: 1 },
    { start: '09:20', end: '10:10', period: 2 },
    { start: '10:10', end: '10:30', period: 0 }, // Short break
    { start: '10:30', end: '11:20', period: 3 },
    { start: '11:20', end: '12:10', period: 4 },
    { start: '12:10', end: '13:00', period: 5 }, // Lunch
    { start: '13:00', end: '13:50', period: 6 },
    { start: '13:50', end: '14:40', period: 7 },
    { start: '14:40', end: '14:50', period: 0 }, // Short break
    { start: '14:50', end: '15:40', period: 8 },
    { start: '15:40', end: '16:30', period: 9 },
  ];

  // Weekly schedule pattern - different subjects for different days
  const weeklySchedule: Record<string, number[]> = {
    [EWeekDay.MONDAY]: [0, 1, 2, 3, 4, -1, 5, 6, -1, 7, 8], // -1 for breaks
    [EWeekDay.TUESDAY]: [1, 2, 3, 4, 5, -1, 6, 7, -1, 8, 0],
    [EWeekDay.WEDNESDAY]: [2, 3, 4, 5, 6, -1, 7, 8, -1, 0, 1],
    [EWeekDay.THURSDAY]: [3, 4, 5, 6, 7, -1, 8, 0, -1, 1, 2],
    [EWeekDay.FRIDAY]: [4, 5, 6, 7, 8, -1, 0, 1, -1, 2, 3],
  };

  // Generate periods for each day (Monday to Friday)
  Object.entries(weeklySchedule).forEach(([day, subjectIndices]) => {
    timeSlots.forEach((slot, slotIndex) => {
      const subjectIndex = subjectIndices[slotIndex];

      if (slot.period === 0) {
        // Short break
        periods.push({
          id: `period_${day}_${slot.start}-${slot.end}`,
          periodNumber: 0,
          startTime: slot.start,
          endTime: slot.end,
          dayOfWeek: day,
          isBreak: true,
          timeSlot: `${slot.start}-${slot.end}`,
        } as IPeriodResponse & { timeSlot: string });
      } else if (subjectIndex === -1) {
        // Lunch break
        periods.push({
          id: `period_${day}_${slot.start}-${slot.end}`,
          periodNumber: slot.period,
          startTime: slot.start,
          endTime: slot.end,
          dayOfWeek: day,
          isBreak: true,
          timeSlot: `${slot.start}-${slot.end}`,
        } as IPeriodResponse & { timeSlot: string });
      } else {
        // Regular class period
        const actualSubjectIndex = subjectIndex % subjects.length;
        periods.push({
          id: `period_${day}_${slot.period}`,
          periodNumber: slot.period,
          startTime: slot.start,
          endTime: slot.end,
          dayOfWeek: day,
          course: {
            id: `course_${actualSubjectIndex + 1}`,
            name: subjects[actualSubjectIndex],
          },
          teacher: {
            id: `teacher_${actualSubjectIndex + 1}`,
            name: teachers[actualSubjectIndex % teachers.length],
          },
          room: rooms[actualSubjectIndex % rooms.length],
          isBreak: false,
          timeSlot: `${slot.start}-${slot.end}`,
        } as IPeriodResponse & { timeSlot: string });
      }
    });
  });

  return periods;
};

export const mockGuardianSchedule: IScheduleResponse[] = [
  {
    id: 'schedule-1',
    name: '7th STD - A Sec',
    type: 'Regular',
    academicYear: '2024-2025',
    term: 'Term 1',
    isActive: true,
    class: {
      id: 'class-7a',
      name: '7th STD',
      section: 'A Sec',
    },
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    periods: generateGuardianSchedulePeriods('7th', 'A'),
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'schedule-2',
    name: '7th STD - B Sec',
    type: 'Regular',
    academicYear: '2024-2025',
    term: 'Term 1',
    isActive: true,
    class: {
      id: 'class-7b',
      name: '7th STD',
      section: 'B Sec',
    },
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    periods: generateGuardianSchedulePeriods('7th', 'B'),
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'schedule-3',
    name: '8th STD - A Sec',
    type: 'Regular',
    academicYear: '2024-2025',
    term: 'Term 1',
    isActive: true,
    class: {
      id: 'class-8a',
      name: '8th STD',
      section: 'A Sec',
    },
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    periods: generateGuardianSchedulePeriods('8th', 'A'),
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'schedule-4',
    name: '9th STD - A Sec',
    type: 'Regular',
    academicYear: '2024-2025',
    term: 'Term 1',
    isActive: true,
    class: {
      id: 'class-9a',
      name: '9th STD',
      section: 'A Sec',
    },
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    periods: generateGuardianSchedulePeriods('9th', 'A'),
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
];

export const mockGuardianClass: IClassResponse = {
  id: 'class-7a',
  name: '7th STD - A Sec',
  description: 'Class 7 Section A',
  section: 'A Sec',
  standardLevel: {
    id: 'std-7',
    name: '7th Standard',
  },
  academicYear: '2024-2025',
  classTutor: {
    id: 'teacher-101',
    name: 'Mrs. Smith',
  },
  strength: 35,
  institute: {
    id: 'institute-1',
    name: 'MONTFORD',
  },
  createdAt: '2024-01-01T00:00:00Z',
  updatedAt: '2024-01-01T00:00:00Z',
};

export const mockGuardianCourses: ICourseResponse[] = [
  {
    id: 'course-1',
    name: 'English',
    description: 'English language and literature',
    subjectCode: 'ENG-07',
    teacher: {
      id: 'teacher-1',
      name: 'Mrs. Smith',
    },
    subjectType: 'Language',
    syllabus: 'Grammar, Literature, Composition',
    totalLectures: 120,
    isElective: false,
    weightage: 100,
    practicalsIncluded: false,
    academicYear: '2024-2025',
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'course-2',
    name: 'Tamil',
    description: 'Tamil language',
    subjectCode: 'TAM-07',
    teacher: {
      id: 'teacher-2',
      name: 'Mr. Kumar',
    },
    subjectType: 'Language',
    syllabus: 'Grammar, Literature, Poetry',
    totalLectures: 100,
    isElective: false,
    weightage: 100,
    practicalsIncluded: false,
    academicYear: '2024-2025',
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'course-3',
    name: 'Mathematics',
    description: 'Mathematics for Class 7',
    subjectCode: 'MATH-07',
    teacher: {
      id: 'teacher-3',
      name: 'Ms. Patel',
    },
    subjectType: 'Mathematics',
    syllabus: 'Algebra, Geometry, Arithmetic',
    totalLectures: 150,
    isElective: false,
    weightage: 100,
    practicalsIncluded: false,
    academicYear: '2024-2025',
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'course-4',
    name: 'Physics',
    description: 'Physics fundamentals',
    subjectCode: 'PHY-07',
    teacher: {
      id: 'teacher-4',
      name: 'Dr. Rao',
    },
    subjectType: 'Science',
    syllabus: 'Mechanics, Heat, Light, Sound',
    totalLectures: 100,
    isElective: false,
    weightage: 100,
    practicalsIncluded: true,
    academicYear: '2024-2025',
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'course-5',
    name: 'Biology',
    description: 'Biology basics',
    subjectCode: 'BIO-07',
    teacher: {
      id: 'teacher-5',
      name: 'Mrs. Sharma',
    },
    subjectType: 'Science',
    syllabus: 'Cell Biology, Plant Kingdom, Animal Kingdom',
    totalLectures: 100,
    isElective: false,
    weightage: 100,
    practicalsIncluded: true,
    academicYear: '2024-2025',
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'course-6',
    name: 'P.E.T',
    description: 'Physical Education and Training',
    subjectCode: 'PET-07',
    teacher: {
      id: 'teacher-6',
      name: 'Mr. Singh',
    },
    subjectType: 'Physical Education',
    syllabus: 'Sports, Fitness, Health',
    totalLectures: 40,
    isElective: false,
    weightage: 50,
    practicalsIncluded: true,
    academicYear: '2024-2025',
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'course-7',
    name: 'French',
    description: 'French language',
    subjectCode: 'FRE-07',
    teacher: {
      id: 'teacher-7',
      name: 'Ms. Desai',
    },
    subjectType: 'Language',
    syllabus: 'Basic French, Grammar, Conversation',
    totalLectures: 80,
    isElective: true,
    weightage: 50,
    practicalsIncluded: false,
    academicYear: '2024-2025',
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'course-8',
    name: 'Hindi',
    description: 'Hindi language',
    subjectCode: 'HIN-07',
    teacher: {
      id: 'teacher-8',
      name: 'Mrs. Reddy',
    },
    subjectType: 'Language',
    syllabus: 'Grammar, Literature, Composition',
    totalLectures: 80,
    isElective: true,
    weightage: 50,
    practicalsIncluded: false,
    academicYear: '2024-2025',
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
];

export const mockGuardianLessonPlans: ILessonPlanApiResponse[] = [
  {
    id: 'lesson-plan-1',
    title: 'Introduction to Algebra',
    description: 'Basic algebra concepts for 7th grade students',
    academicYear: '2024-2025',
    term: 'Term 1',
    board: 'CBSE',
    class: {
      id: 'class-7a',
      name: '7th STD',
      section: 'A Sec',
    },
    course: {
      id: 'course-3',
      name: 'Mathematics',
      subjectCode: 'MATH-07',
    },
    teacher: {
      id: 'teacher-3',
      name: 'Ms. Patel',
    },
    curriculumFramework: 'CBSE',
    syllabusReference: 'Chapter 1-3',
    textbookReference: 'NCERT Class 7, Chapter 1-3',
    ncertChapter: 'Chapter 1-3',
    subject: 'Mathematics',
    gradeLevel: '7th Standard',
    tags: ['algebra', 'mathematics', 'basics'],
    status: ELessonPlanStatus.ACTIVE,
    totalDuration: 15,
    version: 1,
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    units: [],
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'lesson-plan-2',
    title: 'English Literature - Poetry',
    description: 'Introduction to poetry and literary devices',
    academicYear: '2024-2025',
    term: 'Term 1',
    board: 'CBSE',
    class: {
      id: 'class-7a',
      name: '7th STD',
      section: 'A Sec',
    },
    course: {
      id: 'course-1',
      name: 'English',
      subjectCode: 'ENG-07',
    },
    teacher: {
      id: 'teacher-1',
      name: 'Mrs. Smith',
    },
    curriculumFramework: 'CBSE',
    syllabusReference: 'Unit 2',
    textbookReference: 'Honeycomb, Unit 2',
    subject: 'English',
    gradeLevel: '7th Standard',
    tags: ['poetry', 'literature', 'english'],
    status: ELessonPlanStatus.ACTIVE,
    totalDuration: 10,
    version: 1,
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    units: [],
    createdAt: '2024-01-05T00:00:00Z',
    updatedAt: '2024-01-05T00:00:00Z',
  },
  {
    id: 'lesson-plan-3',
    title: 'Physics - Motion and Force',
    description: 'Understanding motion, force, and Newton\'s laws',
    academicYear: '2024-2025',
    term: 'Term 1',
    board: 'CBSE',
    class: {
      id: 'class-7a',
      name: '7th STD',
      section: 'A Sec',
    },
    course: {
      id: 'course-4',
      name: 'Physics',
      subjectCode: 'PHY-07',
    },
    teacher: {
      id: 'teacher-4',
      name: 'Dr. Rao',
    },
    curriculumFramework: 'CBSE',
    syllabusReference: 'Chapter 8-9',
    textbookReference: 'NCERT Class 7, Chapter 8-9',
    subject: 'Physics',
    gradeLevel: '7th Standard',
    tags: ['physics', 'motion', 'force'],
    status: ELessonPlanStatus.APPROVED,
    totalDuration: 12,
    version: 1,
    institute: {
      id: 'institute-1',
      name: 'MONTFORD',
    },
    units: [],
    createdAt: '2024-01-10T00:00:00Z',
    updatedAt: '2024-01-10T00:00:00Z',
  },
];
